import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check } from "lucide-react";

const TIERS = {
  free: {
    price: 0,
    tagline: "For casual users and trying things out.",
    features: ["1,500 words / month", "Up to 800 char input", "3 personal style profiles", "History (30 days)"],
    cta: "Start free",
  },
  pro: {
    price: 12,
    yearly: 115,
    tagline: "For creators, marketers, and students who write daily.",
    features: ["60,000 words / month", "Up to 5,000 char input", "Document uploads (PDF/DOCX)", "Unlimited style profiles", "Full history"],
    cta: "Choose Pro",
  },
  premium: {
    price: 29,
    yearly: 278,
    tagline: "For power users and small teams.",
    features: ["300,000 words / month", "Up to 20,000 char input", "Priority processing", "Everything in Pro", "Team seats (soon)"],
    cta: "Choose Premium",
  },
};

export default function Pricing() {
  const [billing, setBilling] = useState("monthly");
  const [prices, setPrices] = useState([]);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/plans").then(({ data }) => setPrices(data.prices)).catch(() => {}); }, []);

  const startCheckout = async (planKey) => {
    if (!user) { nav("/signup?next=pricing"); return; }
    if (planKey === "free") { nav("/dashboard"); return; }
    const lookup_key = `${planKey}_${billing === "monthly" ? "monthly" : "yearly"}`;
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="text-center max-w-3xl mx-auto">
        <div className="label-eyebrow mb-3">Pricing</div>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight leading-none">Simple pricing.<br/><span className="italic">Real value.</span></h1>
        <p className="mt-6 text-[hsl(var(--muted-foreground))] text-lg">Pick a plan that matches how much you write. Cancel any time.</p>
      </div>

      <div className="mt-10 flex items-center justify-center">
        <div className="pill p-1 gap-1 bg-white">
          {["monthly", "yearly"].map((b) => (
            <button
              key={b}
              data-testid={`billing-${b}`}
              onClick={() => setBilling(b)}
              className={`px-5 py-1.5 text-sm rounded-full transition ${billing === b ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}
            >
              {b === "monthly" ? "Monthly" : "Yearly · save 20%"}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
        {["free", "pro", "premium"].map((key) => {
          const t = TIERS[key];
          const isPro = key === "pro";
          const price = billing === "yearly" && t.yearly ? Math.round(t.yearly / 12) : t.price;
          return (
            <div
              key={key}
              data-testid={`plan-${key}`}
              className={`surface p-8 flex flex-col ${isPro ? "ring-2 ring-[hsl(var(--primary))] relative" : ""}`}
            >
              {isPro && <div className="absolute -top-3 left-8 pill bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]">Most popular</div>}
              <div className="font-display text-2xl capitalize">{key}</div>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="font-display text-5xl">${price}</span>
                <span className="text-sm text-[hsl(var(--muted-foreground))]">/mo</span>
              </div>
              {billing === "yearly" && t.yearly && (
                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Billed ${t.yearly}/year</div>
              )}
              <div className="text-sm text-[hsl(var(--muted-foreground))] mt-3 min-h-[40px]">{t.tagline}</div>
              <ul className="mt-6 space-y-3 text-sm flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex gap-2"><Check size={16} className="text-[hsl(var(--primary))] shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button
                data-testid={`plan-cta-${key}`}
                onClick={() => startCheckout(key)}
                className={`mt-8 ${isPro ? "btn-primary" : "btn-ghost"}`}
              >
                {t.cta}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-16 text-center text-sm text-[hsl(var(--muted-foreground))]">
        Enterprise or high-volume? <Link to="/profile" className="link-underline text-[hsl(var(--primary))]">Contact us</Link>.
      </div>
    </div>
  );
}
